import { get, map, keys, keyBy } from 'lodash';
import { State } from './state';
import {
  Client,
  ClientEngagement,
  ClientEngagementItem,
  ClientWallet,
  InteractionHistory,
  NextInteraction,
  ShareOfWallet,
  ShareOfWalletItem,
} from './types';
import { GetterTree } from 'vuex';
import { config } from '@/services/config';
import { RootState } from '@/store';
import { Header, PaginationOptions, TopHeader } from '@/types/Table';
import {
  formatDataValue,
  formatDateWithTime,
  formatTableValue,
} from '@/helpers/format';
import { FormatOptions } from '@/types/Global';
import {
  createTableHeaders,
  createTopTableHeaders,
  sumColumn,
} from '@/helpers/tables';
import { ClientType, PortfolioStatus, DataType } from '@/enums/Global';
import {
  ClientMetric,
  ClientMetricData,
  YtdKpiTableItem,
  YtdKpiTableData,
  KPITableItem,
  KPITableData,
  LinkClientsTableData,
  LinkClientsTableRow,
  InteractionHistoryTableData,
  RiskProfilesTableData,
  RiskProfilesTableItem,
  OpportunityMatrixTableData,
  OpportunityMatrixTableItem,
} from '@/types/SalesChurn';
import { translations } from '@/plugins/localisation';

export type Getters = {
  getClientData(state: State): Client;
  isClientLoading(state: State): boolean;
  getClientMetricData(
    state: State,
    metricConfig: ClientMetric[]
  ): ClientMetricData;
  getClientStatus(state: State): PortfolioStatus;
  getClientType(state: State): ClientType;
  getYtdKpiTableData(state: State): YtdKpiTableData;
  isYtdKpiLoading(state: State): boolean;
  getClientKpiTableData(state: State, configKey: string): KPITableData;
  getShareOfWalletData(state: State): ShareOfWallet;
  getClientEngagementData(state: State): ClientEngagement;
  getCollectionsLoading(state: State): boolean;
  getPortfolioPerformanceTableData(state: State): KPITableData;
  getNextInteraction(state: State): NextInteraction;
  getInteractionError(state: State): boolean;
  getClientWithLinkedClients(state: State): LinkClientsTableRow;
  getLinkClientsTableData(state: State): LinkClientsTableData;
  isLinkModalLoading(state: State): boolean;
  isLinkModalSubmitting(state: State): boolean;
  isInteractionHistoryLoading(state: State): boolean;
  getInteractionHistory(
    state: State,
    paginationOptions: PaginationOptions
  ): InteractionHistoryTableData;
  getIsGroup(state: State): boolean;
  getRiskProfilesTableData(state: State): RiskProfilesTableData;
  getOpportunityMatrixTableData(state: State): OpportunityMatrixTableData;
  isOpportunityOpen(state: State): (taskId: string) => boolean;
};

export const getters: GetterTree<State, RootState> & Getters = {
  getClientData: state => {
    const client = state.client;
    const group = state.group;
    const stateToUse = group ? group : client;

    return (stateToUse
      ? {
          mainClientId: formatDataValue(stateToUse.mainClientId),
          clientId: formatDataValue(stateToUse.clientId),
          bankerId: formatDataValue(stateToUse.bankerId),
          intermediaryId: formatDataValue(stateToUse.intermediaryId),
          aua: formatDataValue(stateToUse.aua),
          hasInternalFp: formatDataValue(stateToUse.hasInternalFp),
          lastContact: formatDataValue(stateToUse.lastContact),
          performanceVsBenchmark: formatDataValue(
            stateToUse.performanceVsBenchmark
          ),
          persona: formatDataValue(stateToUse.persona),
          personaImage: formatDataValue(stateToUse.personaImage),
          mainServiceType: formatDataValue(stateToUse.mainServiceType),
          clientLabel: formatDataValue(stateToUse.clientLabel),
          clientType: formatDataValue(stateToUse.clientType),
          status: formatDataValue(stateToUse.status),
          age: formatDataValue(stateToUse.age),
          accountOpeningDate: formatDataValue(stateToUse.accountOpeningDate, {
            overrideDateFormat: true,
          }),
          isAtRisk: formatDataValue(stateToUse.isAtRisk),
          isGroup: formatDataValue(stateToUse.isGroup),
          linkedClients:
            stateToUse.linkedClients &&
            stateToUse.linkedClients.map(linkedClient => ({
              clientId: formatDataValue(linkedClient.clientId),
              aumInside: formatDataValue(linkedClient.aumInside),
            })),
        }
      : {}) as Client;
  },
  isClientLoading: state => state.loading,
  getClientMetricData: (state, metricConfig) =>
    metricConfig.reduce((acc: ClientMetricData, m: ClientMetric) => {
      const dataValue = get(state.client, m.key);
      return {
        ...acc,
        ...(dataValue && { [m.key]: dataValue }),
      };
    }, {}),
  getClientStatus: state =>
    get(state.client, 'status.dataValue', PortfolioStatus.UNKNOWN) as PortfolioStatus,
  getClientType: state =>
    get(state.client, 'clientType.dataValue', ClientType.UNKNOWN) as ClientType,
  getYtdKpiTableData: state => {
    const tableData = state.ytdKpiData || [];

    const tableConfig = get(config.layout, 'tableLayouts.ytdKpiTable', {});
    const configTopHeaders = get(tableConfig, 'topHeaders', []) as Array<
      TopHeader
    >;
    const configHeaders = get(tableConfig, 'headers', []) as Array<Header>;
    const configHeadersByKey = keyBy(configHeaders, 'key');

    const items = map(tableData, item => {
      const transformed = Object.assign(
        {},
        ...Object.entries(item.clientSpecificValues).map(item => ({
          [item[0]]: formatDataValue(item[1]),
        }))
      );
      transformed.roa = formatDataValue(item.clientSpecificValues.roa, {
        keepZeroAsZero: true,
      } as FormatOptions); // we want roa to show up as 0 instead of a dash in the front end
      transformed.revenue = formatDataValue(item.clientSpecificValues.revenue, {
        keepZeroAsZero: true,
      } as FormatOptions); // we want revenue to show up as 0 instead of a dash in the front end
      return {
        aum: formatDataValue(item.aum),
        familyId: formatDataValue(item.familyId),
        portfolioId: formatDataValue(item.portfolioId),
        ...transformed,
      } as YtdKpiTableItem;
    });

    const clientSpecificFooter = keys(
      get(tableData[0], 'clientSpecificValues', {})
    ).reduce((acc: { [x: string]: string }, col: string) => {
      return configHeadersByKey[col] &&
        configHeadersByKey[col].excludeFromFooter
        ? acc
        : {
            ...acc,
            [col]: formatTableValue(
              sumColumn(tableData, col, true),
              get(tableData[0], `clientSpecificValues.${col}.dataType`) as DataType
            ),
          };
    }, {} as { [key: string]: string });

    const revenue = sumColumn(tableData, 'referenceRevenue', true);
    const assets = sumColumn(tableData, 'referenceAssets', true);
    clientSpecificFooter['roa'] = formatTableValue(
      assets !== 0 ? revenue / assets : 0,
      get(tableData[0], 'clientSpecificValues.roa.dataType') as DataType
    );

    const footer = {
      aum: formatTableValue(sumColumn(tableData, 'aum')),
      familyId: '',
      portfolioId: 'grandTotal',
      ...clientSpecificFooter,
    };

    return {
      topHeaders: createTopTableHeaders(configTopHeaders),
      headers: createTableHeaders(configHeaders),
      items,
      footer,
    };
  },
  isYtdKpiLoading: state => state.ytdKpiDataIsLoading,
  getClientKpiTableData: (state, configKey) => {
    const tableConfig = get(config.layout, `tableLayouts.${configKey}`, {});
    const headers = get(tableConfig, 'mainHeaders', []);
    const currentYear = new Date().getFullYear();
    const groupExists = !!state.group;
    const data = groupExists
      ? get(state.group, 'statsByYear', {})
      : get(state.client, 'statsByYear', {});
    const keys: string[] = get(
      config.layout,
      `tableLayouts.${configKey}.rowKeys`,
      []
    );

    const items = keys.map(key => {
      return {
        category: key,
        description: key,
        ytd: formatDataValue(get(data, [currentYear, key])),
        year1: formatDataValue(get(data, [currentYear - 1, key])),
        year2: formatDataValue(get(data, [currentYear - 2, key])),
      } as KPITableItem;
    });

    return {
      headers: createTableHeaders(headers),
      items,
    };
  },
  getPortfolioPerformanceTableData: state => {
    const currentYear = new Date().getFullYear();
    const collections = state.collections;
    const keys: string[] = get(
      config.layout,
      `tableLayouts.portfolioPerformanceTable.rowKeys`,
      []
    );
    const tableConfig = get(
      config.layout,
      `tableLayouts.portfolioPerformanceTable`,
      {}
    );
    const headers = get(tableConfig, 'mainHeaders', []);

    const items = collections.flatMap(collection => {
      return keys.map((key, index) => {
        return {
          category: index > 0 ? '' : collection.collectionId,
          description: key,
          ytd: formatDataValue(get(collection.statsByYear, [currentYear, key])),
          year1: formatDataValue(
            get(collection.statsByYear, [currentYear - 1, key])
          ),
          year2: formatDataValue(
            get(collection.statsByYear, [currentYear - 2, key])
          ),
        } as KPITableItem;
      });
    });

    return {
      headers: createTableHeaders(headers),
      items,
    };
  },
  getRiskProfilesTableData: state => {
    const riskProfiles = state.client.riskProfiles;
    const tableConfig = get(
      config.layout,
      'tableLayouts.riskProfilesTable',
      {}
    );

    const headers = get(tableConfig, 'mainHeaders', []);

    const items = Object.keys(riskProfiles).map(key => {
      const profile = riskProfiles[key];
      return {
        profile: key,
        containerCount: formatDataValue(profile.containerCount),
        containerAumGbp: `${formatDataValue(
          profile.containerAumGbp
        )} (${formatDataValue(profile.percentAum)})`,
        collectionPerformanceCurrentYear: formatDataValue(
          profile.collectionPerformanceCurrentYear
        ),
        deltaVersusBenchmarkCurrentYear: formatDataValue(
          profile.deltaVersusBenchmarkCurrentYear
        ),
      } as RiskProfilesTableItem;
    });

    return {
      headers: createTableHeaders(headers),
      items,
    };
  },

  getOpportunityMatrixTableData: state => {
    const opportunityMatrix = state.client.opportunityMatrix;
    const tableConfig = get(
      config.layout,
      'tableLayouts.opportunityMatrixTable',
      {}
    );

    const headers = get(tableConfig, 'mainHeaders', []);

    const items = Object.keys(opportunityMatrix).map(key => {
      const opportunity = opportunityMatrix[key];
      return {
        opportunity: key,
        TODO: formatDataValue(opportunity.TODO),
        NOT_STARTED: formatDataValue(opportunity.NOT_STARTED),
        IN_PROGRESS: formatDataValue(opportunity.IN_PROGRESS),
        DONE: formatDataValue(opportunity.DONE),
        OVERDUE: formatDataValue(opportunity.OVERDUE),
      } as OpportunityMatrixTableItem;
    });

    return {
      headers: createTableHeaders(headers),
      items,
    };
  },
  getShareOfWalletData: state => {
    const aumOutsideByCompanyStateItem = state.group
      ? state.group.aumOutsideByCompany
      : state.client.aumOutsideByCompany;
    const ratioStateItem = state.group
      ? state.group.aumRatio
      : state.client.aumRatio;
    const aumOutside = state.group
      ? state.group.aumOutside
      : state.client.aumOutside;

    const clientWallets: ClientWallet[] = [];
    for (const [key, value] of Object.entries(aumOutsideByCompanyStateItem ?? {})) {
      const assets = value.filter(item => item.aumType.dataValue === 'ASSETS');
      const liabilities = value.filter(
        item => item.aumType.dataValue === 'LIABILITIES'
      );

      clientWallets.push({
        clientId: key,
        assets: assets.map(item => ({
          type: formatDataValue(item.productType),
          company: formatDataValue(item.company),
          value: formatDataValue(item.aum),
          note: formatDataValue(item.note, { keepZeroAsZero: true }),
        })) as ShareOfWalletItem[],
        liabilities: liabilities.map(item => ({
          type: formatDataValue(item.productType),
          company: formatDataValue(item.company),
          value: formatDataValue(item.aum),
          note: formatDataValue(item.note, { keepZeroAsZero: true }),
        })) as ShareOfWalletItem[],
      });
    }

    return {
      ratio: formatDataValue(ratioStateItem, {
        keepZeroAsZero: true,
      }) as number,
      clients: clientWallets,
      aumOutside: formatDataValue(aumOutside) as string,
    };
  },
  getClientEngagementData: state => {
    const { t } = translations.global;
    const stateItem = state.group ? state.group : state.client;

    const qualityItem =
      stateItem?.clientEngagement?.find(
        item => item.type.dataValue === 'QUALITY'
      );
    const allItem =
      stateItem?.clientEngagement?.find(item => item.type.dataValue === 'ALL');
    const platformItem =
      stateItem?.clientEngagement?.find(
        item => item.type.dataValue === 'PLATFORM'
      );

    const lastQuality =
      qualityItem &&
      ((qualityItem.daysSinceLastContact.dataValue as number) > 365 ||
      qualityItem.daysSinceLastContact.dataValue == null
        ? `> 365 ${t('general.days-ago')}`
        : `${formatDataValue(qualityItem.daysSinceLastContact, { keepZeroAsZero: true })} ${t(
            'general.days-ago'
          )}`);
    const lastAll =
      allItem &&
      ((allItem.daysSinceLastContact.dataValue as number) > 365 ||
      allItem.daysSinceLastContact.dataValue == null
        ? `> 365 ${t('general.days-ago')}`
        : `${formatDataValue(allItem.daysSinceLastContact, { keepZeroAsZero: true })} ${t(
            'general.days-ago'
          )}`);
    const lastPlatform =
      platformItem &&
      ((platformItem.daysSinceLastContact.dataValue as number) > 365
        ? `> 365 ${t('general.days-ago')}`
        : `${formatDataValue(platformItem.daysSinceLastContact, { keepZeroAsZero: true })} ${t(
            'general.days-ago'
          )}`);

    return {
      quality: {
        last: lastQuality,
        last3Months: qualityItem && formatDataValue(qualityItem.inMonths3),
        last12Months: qualityItem && formatDataValue(qualityItem.inMonths12),
      } as ClientEngagementItem,
      all: {
        last: lastAll,
        last3Months: allItem && formatDataValue(allItem.inMonths3),
        last12Months: allItem && formatDataValue(allItem.inMonths12),
      } as ClientEngagementItem,
      platform: {
        last: lastPlatform,
        last3Months: platformItem && formatDataValue(platformItem.inMonths3),
        last12Months: platformItem && formatDataValue(platformItem.inMonths12),
      } as ClientEngagementItem,
    };
  },
  getCollectionsLoading: state => state.collectionsLoading,
  getNextInteraction: state => state.nextInteraction,
  getInteractionError: state => state.interactionError,
  getClientWithLinkedClients: state => {
    const stateItem = state.group ? state.group : state.client;

    return {
      clientId: formatDataValue(stateItem.clientId),
      aumInside: formatDataValue(stateItem.aumInside),
      linkedClients: stateItem.linkedClients.map(linkedClient => ({
        clientId: formatDataValue(linkedClient.clientId),
        aumInside: formatDataValue(linkedClient.aumInside),
      })),
    } as LinkClientsTableRow;
  },
  getLinkClientsTableData: state => {
    const tableConfig = get(config.layout, `tableLayouts.linkClientsTable`, {});
    const headers = get(tableConfig, 'headers', []);

    const items: LinkClientsTableRow[] = state.linkableClients.map(client => {
      return {
        clientId: formatDataValue(client.client.clientId),
        aumInside: formatDataValue(client.client.aumInside),
        linkedClients: client.linkedClients.map(linkedClient => ({
          clientId: formatDataValue(linkedClient.clientId),
          aumInside: formatDataValue(linkedClient.aumInside),
        })),
      } as LinkClientsTableRow;
    });

    return {
      items,
      headers: createTableHeaders(headers),
    };
  },
  isLinkModalLoading: state => {
    return state.linkModalLoading;
  },
  isLinkModalSubmitting: state => {
    return state.linkModalSubmitting;
  },
  isInteractionHistoryLoading: state => {
    return state.interactionHistoryLoading;
  },
  getInteractionHistory: (state, paginationOptions) => {
    const headerConfig = get(
      config.layout,
      'tableLayouts.interactionHistoryTable.headers',
      []
    );

    const items = state.interactionHistory.slice(
      (paginationOptions.pageNo - 1) * paginationOptions.pageSize,
      paginationOptions.pageNo * paginationOptions.pageSize
    );

    return {
      items: items.map(
        item =>
          ({
            bankerId: formatDataValue(item.bankerId),
            interactionDate: formatDateWithTime(item.interactionDate),
            actionsDiscussed: item.actionsDiscussed.map(action =>
              formatDataValue(action)
            ),
            interactionType: formatDataValue(item.interactionType),
            referralRequest: formatDataValue(item.referralRequest),
            note: formatDataValue(item.note),
          } as InteractionHistory)
      ),
      headers: createTableHeaders(headerConfig),
      count: state.interactionHistory.length,
    };
  },
  getIsGroup: state => {
    return !!state.group;
  },
  isOpportunityOpen: state => (taskId: string) => {
    return state.opportunityOpen[taskId] || false;
  },
};
